import React, { useState, useEffect } from "react";
import { track } from "/src/helpers/analytics";
import { useOutletContext, useNavigate, useLocation } from "react-router";
import formatMoney from "/src/helpers/formatMoney";
import { useApi } from "/src/helpers/Hooks/useApi";

const PaymentPage = () => {
    const { model, refreshModel } = useOutletContext();
    const navigate = useNavigate();
    const location = useLocation();
    const api = useApi();
    const [amount, setAmount] = useState("");
    const [countries, setCountries] = useState([]);
    const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

    useEffect(() => {
        if (location.state?.paymentSuccess) {
            setShowPaymentSuccess(true);
            refreshModel();
            // Clear the state so the message doesn't reappear on refresh
            window.history.replaceState({}, '');
        }
    }, []);
    const [error, setError] = useState("");
    const [showConfirm, setShowConfirm] = useState(false);
    const [editing, setEditing] = useState(false);
    const [details, setDetails] = useState({
        firstName: "",
        lastName: "",
        email: "",
        addressLine1: "",
        addressLine2: "",
        townCity: "",
        postcode: "",
        countryCode: "",
    });

    const payments = model?.payments || [];
    const outstanding = model?.totalOutstanding || 0;
    // More than one payment still to come = an instalments ("pay monthly") plan; a single balance
    // is already covered by the "Due by" line.
    const scheduled = outstanding > 0 ? model?.scheduledPayments || [] : [];
    const payingMonthly = scheduled.length > 1;
    const formatDate = (date) => new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

    useEffect(() => {
        const fetchCountries = async () => {
            const result = await api.getCountries();
            if (result) setCountries(result);
        };
        fetchCountries();
    }, []);

    const handleStartPayment = () => {
        setError("");
        const value = parseFloat(amount);
        if (!amount || isNaN(value) || value <= 0) {
            setError("Please enter a valid amount.");
            return;
        }
        if (value > outstanding) {
            setError(`Amount cannot exceed the outstanding balance of £${formatMoney(outstanding)}.`);
            return;
        }
        setDetails({
            firstName: model?.customerDetails?.firstName || "",
            lastName: model?.customerDetails?.lastName || "",
            email: model?.customerDetails?.email || "",
            addressLine1: model?.customerDetails?.address1 || "",
            addressLine2: model?.customerDetails?.address2 || "",
            townCity: model?.customerDetails?.townCity || "",
            postcode: model?.customerDetails?.postcode || "",
            countryCode: model?.customerDetails?.countryCode || "",
        });
        setEditing(false);
        setShowConfirm(true);
    };

    const handleConfirmPayment = () => {
        if (!details.firstName || !details.lastName || !details.addressLine1 || !details.townCity || !details.postcode || !details.countryCode) {
            setError("Please fill in all required fields.");
            return;
        }
        setError("");
        // Not an upsell: plain custom event (no ecommerce params) so it never counts as revenue.
        const paying = parseFloat(amount);
        track("balance_payment_started", {
            payment_type: "balance", currency: "GBP", value: paying,
            outstanding_before: outstanding, full_payment: paying >= outstanding - 0.005
        });
        navigate("/payment/checkout", { state: { amount: paying, details, outstanding } });
    };

    const handleDetailChange = (field, value) => {
        setDetails((prev) => ({ ...prev, [field]: value }));
    };

    const inputClass = "w-full px-4 py-2.5 border border-input_border rounded-lg text-primary placeholder-content_3 focus:outline-none focus:ring-2 focus:ring-emp focus:border-transparent transition text-sm";

    return (
        <div>
            <h1 className="text-3xl font-poppins font-semibold text-primary mb-6">Payments</h1>

            {showPaymentSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-card p-5 mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-green-800 font-medium text-sm">Thank you for your payment! It has been received successfully.</p>
                    </div>
                    <button onClick={() => setShowPaymentSuccess(false)} className="text-green-400 hover:text-green-600 transition cursor-pointer">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

          

            {/* Previous Payments */}
            <div className="bg-white rounded-card p-5 mb-6">
                <h2 className="text-lg font-semibold text-primary font-poppins mb-4">Payment Overview</h2>

                {payments.length === 0 ? (
                    <p className="text-sm text-content_2">No payments have been made yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead>
                                <tr className="border-b border-content_5">
                                    <th className="py-3 pr-4 font-semibold text-content_2">Payment Date</th>
                                    <th className="py-3 text-right font-semibold text-content_2">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((payment, index) => (
                                    <tr key={index} className="border-b border-content_5 last:border-0">
                                        <td className="py-3 pr-4 text-primary">
                                            {new Date(payment.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}{" "}
                                            {/*<span className="text-content_3">
                                                {new Date(payment.date).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                                            </span>*/}  
                                        </td>
                                        <td className="py-3 text-right font-semibold text-primary">£{formatMoney(payment.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {payments.length > 0 && (
                    <div className="flex justify-between items-center pt-3 mt-3 border-t border-content_4">
                        <span className="font-semibold text-primary">Total Paid</span>
                        <span className="font-semibold text-primary">£{formatMoney(model.totalPaid)}</span>
                    </div>
                )}

                <div className="flex justify-between items-center pt-3 mt-3 border-t border-content_4">
                    <span className="font-semibold text-primary">Holiday Total</span>
                    <span className="font-semibold text-primary">£{formatMoney(model.totalPrice)}</span>
                </div>
                <div className="flex justify-between items-center pt-2 mt-3 border-t border-content_4">
                    <div>
                        <span className="font-semibold text-primary">Outstanding Balance</span>
                        {outstanding > 0 && payingMonthly && (
                            <span className="block text-xs text-content_3 mt-0.5">
                                Paid monthly, see upcoming payments below
                            </span>
                        )}
                        {outstanding > 0 && !payingMonthly && model.balanceDueDate && (
                            <span className="block text-xs text-content_3 mt-0.5">
                                Due by {model.balanceDueDate}
                            </span>
                        )}
                    </div>
                    <span className={`font-semibold ${outstanding > 0 ? "text-red-600" : "text-green-600"}`}>
                        £{formatMoney(outstanding)}
                    </span>
                </div>
            </div>

            {/* Upcoming Payments (instalments plan) */}
            {payingMonthly && (
                <div className="bg-white rounded-card p-5 mb-6">
                    <h2 className="text-lg font-semibold text-primary font-poppins mb-4">Upcoming Payments</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead>
                                <tr className="border-b border-content_5">
                                    <th className="py-3 pr-4 font-semibold text-content_2">Due Date</th>
                                    <th className="py-3 text-right font-semibold text-content_2">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {scheduled.map((payment, index) => (
                                    <tr key={index} className="border-b border-content_5 last:border-0">
                                        <td className="py-3 pr-4 text-primary">{formatDate(payment.dueDate)}</td>
                                        <td className="py-3 text-right font-semibold text-primary">£{formatMoney(payment.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Make a Payment */}
            {outstanding > 0 && (
                <div className="bg-white rounded-card p-5 mb-6 border-2 border-emp shadow-md overflow-hidden">
                    <div
                        key={showConfirm ? "confirm" : "amount"}
                        className="animate-fadeSlideIn"
                    >
                    {!showConfirm ? (
                        <>
                            <h2 className="text-lg font-semibold text-primary font-poppins mb-1">Make a Payment</h2>
                            <p className="text-sm text-content_2 mb-4">
                                Outstanding balance: <span className="font-semibold text-primary">£{formatMoney(outstanding)}</span>
                            </p>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <div className="relative flex-1">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-content_3 font-medium">£</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        max={outstanding}
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder="0.00"
                                        className="w-full pl-8 pr-4 py-3 border border-input_border rounded-lg text-primary placeholder-content_3 focus:outline-none focus:ring-2 focus:ring-emp focus:border-transparent transition"
                                    />
                                </div>
                                <button
                                    onClick={handleStartPayment}
                                    className="px-6 py-3 bg-emp hover:bg-emp_dark text-white font-semibold rounded-lg transition cursor-pointer shrink-0"
                                >
                                    Start Payment
                                </button>
                            </div>

                            {error && (
                                <div className="bg-error_2 text-error text-sm rounded-lg px-4 py-3 mt-3">
                                    {error}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-1">
                                <h2 className="text-lg font-semibold text-primary font-poppins">Confirm Your Details</h2>
                                {!editing && (
                                    <button
                                        onClick={() => setEditing(true)}
                                        className="text-sm text-emp hover:text-emp_dark font-medium transition cursor-pointer"
                                    >
                                        Edit
                                    </button>
                                )}
                            </div>
                            <p className="text-sm text-content_2 mb-4">
                                Payment amount: <span className="font-semibold text-primary">£{formatMoney(parseFloat(amount))}</span>
                            </p>

                            {editing ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-content_2 mb-1">First Name *</label>
                                        <input type="text" value={details.firstName} onChange={(e) => handleDetailChange("firstName", e.target.value)} className={inputClass} placeholder="First name" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-content_2 mb-1">Last Name *</label>
                                        <input type="text" value={details.lastName} onChange={(e) => handleDetailChange("lastName", e.target.value)} className={inputClass} placeholder="Last name" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="block text-xs font-medium text-content_2 mb-1">Email</label>
                                        <input type="email" value={details.email} onChange={(e) => handleDetailChange("email", e.target.value)} className={inputClass} placeholder="Email address" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="block text-xs font-medium text-content_2 mb-1">Address Line 1 *</label>
                                        <input type="text" value={details.addressLine1} onChange={(e) => handleDetailChange("addressLine1", e.target.value)} className={inputClass} placeholder="Address line 1" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="block text-xs font-medium text-content_2 mb-1">Address Line 2</label>
                                        <input type="text" value={details.addressLine2} onChange={(e) => handleDetailChange("addressLine2", e.target.value)} className={inputClass} placeholder="Address line 2" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-content_2 mb-1">Town / City *</label>
                                        <input type="text" value={details.townCity} onChange={(e) => handleDetailChange("townCity", e.target.value)} className={inputClass} placeholder="Town or city" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-content_2 mb-1">Postcode *</label>
                                        <input type="text" value={details.postcode} onChange={(e) => handleDetailChange("postcode", e.target.value)} className={inputClass} placeholder="Postcode" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-content_2 mb-1">Country *</label>
                                        <select value={details.countryCode} onChange={(e) => handleDetailChange("countryCode", e.target.value)} className={inputClass}>
                                            <option value="">Select a country</option>
                                            {countries.map((c) => (
                                                <option key={c.code} value={c.code}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
                                    <div><span className="text-content_2">Name:</span> <span className="text-primary font-medium">{details.firstName} {details.lastName}</span></div>
                                    {details.email && <div><span className="text-content_2">Email:</span> <span className="text-primary font-medium">{details.email}</span></div>}
                                    <div className="sm:col-span-2"><span className="text-content_2">Address:</span> <span className="text-primary font-medium">{details.addressLine1}{details.addressLine2 ? `, ${details.addressLine2}` : ""}, {details.townCity}, {details.postcode}</span></div>
                                    <div><span className="text-content_2">Country:</span> <span className="text-primary font-medium">{countries.find(c => c.code === details.countryCode)?.name || details.countryCode}</span></div>
                                </div>
                            )}

                            {error && (
                                <div className="bg-error_2 text-error text-sm rounded-lg px-4 py-3 mt-3">
                                    {error}
                                </div>
                            )}

                            <div className="flex justify-between mt-4">
                                <button
                                    onClick={() => { setShowConfirm(false); setEditing(false); setError(""); }}
                                    className="px-6 py-2.5 border border-content_4 text-content_1 font-semibold rounded-lg transition hover:bg-content_5 cursor-pointer"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleConfirmPayment}
                                    className="px-6 py-2.5 bg-emp hover:bg-emp_dark text-white font-semibold rounded-lg transition cursor-pointer"
                                >
                                    Continue to Payment
                                </button>
                            </div>
                        </>
                    )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentPage;
